import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Loader2, Plus, Globe, CheckCircle, XCircle, Clock, Info, AlertTriangle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CommonService } from "@/services";
import { IRootReducerState } from "@/store/reducers";
import "./DomainsSettings.scss";

interface Certificate {
    id?: string;
    hostname: string;
    clientStatus?: string;
    createdAt?: string;
    error_message?: string;
    CNAME?: {
        hostname: string;
        target: string;
        instructions: string;
    };
}

interface DNSProvider {
    id: string;
    name: string;
    steps: string[];
    fields: { label: string; value: string }[];
    notes?: string[];
}

const DNS_PROVIDERS: DNSProvider[] = [
    {
        id: 'cloudflare',
        name: 'Cloudflare',
        steps: [
            'Login to Cloudflare',
            'Select your Domain',
            'Click DNS (left sidebar)',
            'Click Add record',
            'Select Type → CNAME',
            'Fill in the details below',
            'Set Proxy status to "DNS only" (gray cloud)',
            'Click Save'
        ],
        fields: [
            { label: 'Type', value: 'CNAME' },
            { label: 'Name', value: 'Your subdomain (e.g., www or @)' },
            { label: 'Target', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Auto' }
        ],
        notes: ['⚠️ Turn Proxy OFF (gray cloud) for proper DNS resolution']
    },
    {
        id: 'godaddy',
        name: 'GoDaddy',
        steps: [
            'Login to GoDaddy',
            'Go to My Products',
            'Click DNS next to your domain',
            'Click Add',
            'Select CNAME',
            'Fill in the details',
            'Click Save'
        ],
        fields: [
            { label: 'Host', value: 'Your subdomain (e.g., www)' },
            { label: 'Points to', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: '1 hour / Default' }
        ]
    },
    {
        id: 'namecheap',
        name: 'Namecheap',
        steps: [
            'Login to Namecheap',
            'Click Domain List',
            'Click Manage on your domain',
            'Go to Advanced DNS tab',
            'Click Add New Record',
            'Select CNAME Record',
            'Fill in the details',
            'Click Save'
        ],
        fields: [
            { label: 'Host', value: 'Your subdomain (e.g., www)' },
            { label: 'Value', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Automatic' }
        ]
    },
    {
        id: 'route53',
        name: 'AWS Route 53',
        steps: [
            'Open AWS Console',
            'Go to Route 53',
            'Click Hosted Zones',
            'Select your domain',
            'Click Create record',
            'Choose CNAME record type',
            'Fill in the details',
            'Click Create records'
        ],
        fields: [
            { label: 'Record name', value: 'Your subdomain (e.g., www)' },
            { label: 'Record type', value: 'CNAME' },
            { label: 'Value', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: '300' }
        ]
    },
    {
        id: 'google',
        name: 'Google Domains / Cloud DNS',
        steps: [
            'Login to Google Domains',
            'Select your domain',
            'Go to DNS settings',
            'Scroll to Custom Records',
            'Click Add CNAME record',
            'Fill in the details',
            'Click Save'
        ],
        fields: [
            { label: 'Name', value: 'Your subdomain (e.g., www)' },
            { label: 'Type', value: 'CNAME' },
            { label: 'Data', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Default' }
        ]
    },
    {
        id: 'hostinger',
        name: 'Hostinger',
        steps: [
            'Login to Hostinger',
            'Go to Domains',
            'Select your domain',
            'Click DNS / Nameservers',
            'Choose DNS Records',
            'Click Add new record',
            'Fill in the details',
            'Click Save'
        ],
        fields: [
            { label: 'Type', value: 'CNAME' },
            { label: 'Name', value: 'Your subdomain (e.g., www)' },
            { label: 'Points to', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Auto' }
        ]
    },
    {
        id: 'porkbun',
        name: 'Porkbun',
        steps: [
            'Login to Porkbun',
            'Go to Domain Management',
            'Select your domain',
            'Click DNS Records',
            'Click Add record',
            'Select CNAME',
            'Fill in the details',
            'Click Save'
        ],
        fields: [
            { label: 'Type', value: 'CNAME' },
            { label: 'Host', value: 'Your subdomain (e.g., www)' },
            { label: 'Answer', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Default' }
        ]
    },
    {
        id: 'dynadot',
        name: 'Dynadot',
        steps: [
            'Login to Dynadot',
            'Go to My Domains',
            'Click Manage Domains',
            'Click your domain name',
            'Open DNS Settings',
            'Select Dynadot DNS',
            'Scroll to CNAME Records',
            'Add record and Save'
        ],
        fields: [
            { label: 'Subdomain', value: 'Your subdomain (e.g., www)' },
            { label: 'Target Host', value: 'Your deployment CNAME URL' }
        ]
    },
    {
        id: 'digitalocean',
        name: 'DigitalOcean DNS',
        steps: [
            'Open DigitalOcean',
            'Go to Networking',
            'Click Domains',
            'Select your domain',
            'Click Add record',
            'Select CNAME',
            'Fill in the details',
            'Click Create Record'
        ],
        fields: [
            { label: 'Type', value: 'CNAME' },
            { label: 'Hostname', value: 'Your subdomain (e.g., www)' },
            { label: 'Will direct to', value: 'Your deployment CNAME URL' },
            { label: 'TTL', value: 'Default' }
        ]
    }
];

const DomainsSettings = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const { currentProjectDetails } = useSelector((state: IRootReducerState) => state.projectData);
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [cnameUrl, setCnameUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [newHostname, setNewHostname] = useState("");
    const [error, setError] = useState("");
    const [selectedProvider, setSelectedProvider] = useState<string>("");
    const [activeTab, setActiveTab] = useState<string>("domains");

    // Check if project is published (has a subdomain_url)
    const isPublished = !!currentProjectDetails?.subdomain_url;


    const fetchCertificates = useCallback(async () => {
        if (!projectId) return;

        setIsLoading(true);
        setError("");

        try {
            const response = await CommonService._project_service.getFlyCertificates(projectId);

            // Response structure: { app_name, certificates, cname_url }
            const certs = response?.data?.certificates || [];
            const cname = response?.data?.cname_url || "";

            setCertificates(certs);
            setCnameUrl(cname);

            CommonService.handleMixpanelTrack("domains_settings_fetched", {
                project_id: projectId,
                certificate_count: certs.length,
                timestamp: new Date().toISOString()
            });
        } catch (err: any) {
            console.error("Error fetching certificates:", err);
            setError(err?.response?.data?.message || "Failed to load certificates");
            CommonService._alert.showToast("Failed to load certificates", "error");
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    // Fetch certificates on mount
    useEffect(() => {
        if (projectId && isPublished) {
            fetchCertificates();
        }
    }, [projectId, isPublished, fetchCertificates]);


    const handleAddDomain = async () => {
        if (!newHostname.trim()) {
            setError("Please enter a valid hostname");
            return;
        }

        // Basic hostname validation
        const hostnameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!hostnameRegex.test(newHostname.trim())) {
            setError("Please enter a valid domain name (e.g., example.com or subdomain.example.com)");
            return;
        }

        setIsAdding(true);
        setError("");

        try {
            await CommonService._project_service.createFlyCertificate(projectId!, {
                hostname: newHostname.trim()
            });

            CommonService._alert.showToast("Domain added successfully! SSL certificate provisioning started.", "success");
            CommonService.handleMixpanelTrack("domain_certificate_added", {
                project_id: projectId,
                hostname: newHostname.trim(),
                timestamp: new Date().toISOString()
            });

            // Clear input and refresh list
            setNewHostname("");
            await fetchCertificates();
        } catch (err: any) {
            console.error("Error adding certificate:", err);
            const errorMessage = err?.response?.data?.message || err?.response?.data?.detail || "Failed to add domain";
            setError(errorMessage);
            CommonService._alert.showToast(errorMessage, "error");
            CommonService.handleMixpanelTrack("domain_certificate_add_error", {
                project_id: projectId,
                hostname: newHostname.trim(),
                error: errorMessage,
                timestamp: new Date().toISOString()
            });
        } finally {
            setIsAdding(false);
        }
    };

    const getStatusIcon = (status?: string) => {
        if (!status) return <Globe className="w-5 h-5 text-gray-500" />;

        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes('ready') || lowerStatus.includes('issued')) {
            return <CheckCircle className="w-5 h-5 text-green-500" />;
        }
        if (lowerStatus.includes('error') || lowerStatus.includes('failed')) {
            return <XCircle className="w-5 h-5 text-red-500" />;
        }
        if (lowerStatus.includes('awaiting') || lowerStatus.includes('pending')) {
            return <Clock className="w-5 h-5 text-yellow-500" />;
        }
        return <Globe className="w-5 h-5 text-gray-500" />;
    };

    const getStatusText = (status?: string) => {
        return status || 'Unknown';
    };

    const handleCopy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            CommonService._alert.showToast(`${label} copied to clipboard!`, "success");
            CommonService.handleMixpanelTrack("dns_value_copied", {
                project_id: projectId,
                label: label,
                timestamp: new Date().toISOString()
            });
        } catch {
            CommonService._alert.showToast("Failed to copy to clipboard", "error");
        }
    };

    return (
        <div className="domains-settings">
            <div className="settings-header">
                <h2>Custom Domains</h2>
                <p className="text-muted">Configure custom domains and SSL certificates for your application</p>
            </div>

            {!isPublished ? (
                // Show warning when project is not published
                <div className="alert-box warning-alert">
                    <AlertTriangle className="h-5 w-5" />
                    <div className="alert-content">
                        <strong>Project Not Published</strong>
                        <p className="mt-1">
                            You need to publish your project before you can add custom domains.
                            Click the "Publish" button in the top bar to deploy your application first.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="domains-tabs">
                        <TabsList className="mb-6">
                            <TabsTrigger value="domains">Domains</TabsTrigger>
                            <TabsTrigger value="guide">Guide</TabsTrigger>
                        </TabsList>

                        <TabsContent value="domains">
                            {/* DNS Instructions */}
                            <div className="alert-box info-alert mb-6">
                                <Info className="h-4 w-4" />
                                <div className="alert-content">
                                    <strong>Before adding a domain:</strong> Make sure to configure your DNS records to point to your application.
                                    You'll need to add a CNAME record for your domain.
                                    <p className="mt-2 text-sm">
                                        <strong>Note:</strong> DNS changes can take 5-10 minutes to propagate, but in some cases may take up to 24 hours depending on your DNS provider and TTL settings.
                                    </p>
                                </div>
                            </div>

                            {/* Add Domain Form */}
                            <Card className="add-domain-card">
                                <div className="card-content">
                                    <h3 className="text-lg font-semibold mb-4">Add Custom Domain</h3>

                                    <div className="add-domain-form">
                                        <div className="form-group">
                                            <Label htmlFor="hostname">Domain Name</Label>
                                            <Input
                                                id="hostname"
                                                type="text"
                                                placeholder="example.com"
                                                value={newHostname}
                                                onChange={(value: string) => {
                                                    setNewHostname(value);
                                                    setError("");
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddDomain();
                                                    }
                                                }}
                                                disabled={isAdding}
                                            />
                                        </div>

                                        <Button
                                            onClick={handleAddDomain}
                                            disabled={isAdding || !newHostname.trim()}
                                            className="add-domain-button"
                                        >
                                            {isAdding ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Adding...
                                                </>
                                            ) : (
                                                <>
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    Add Domain
                                                </>
                                            )}
                                        </Button>
                                    </div>

                                    {error && (
                                        <div className="alert-box error-alert mt-4">
                                            <div className="alert-content">{error}</div>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* Certificates List */}
                            <div className="certificates-section">
                                <h3 className="text-lg font-semibold mb-4">Configured Domains</h3>

                                {isLoading ? (
                                    <div className="loading-state">
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        <p>Loading certificates...</p>
                                    </div>
                                ) : certificates.length === 0 ? (
                                    <div className="empty-state">
                                        <Globe className="w-12 h-12 text-gray-400 mb-3" />
                                        <p className="text-muted">No custom domains configured yet</p>
                                        <p className="text-sm text-muted">Add your first custom domain above to get started</p>
                                    </div>
                                ) : (
                                    <div className="certificates-list">
                                        {certificates.map((cert, index) => (
                                            <Card key={cert.id || cert.hostname || index} className="certificate-card">
                                                <div className="certificate-info">
                                                    <div className="certificate-header">
                                                        {getStatusIcon(cert.clientStatus)}
                                                        <div className="certificate-details">
                                                            <h4 className="certificate-hostname">{cert.hostname}</h4>
                                                            <span className={`certificate-status status-${cert.clientStatus?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}>
                                                                {getStatusText(cert.clientStatus)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Show CNAME configuration instructions */}
                                                    {cert.CNAME && cert.clientStatus?.toLowerCase().includes('awaiting') && (
                                                        <div className="alert-box info-alert mt-3">
                                                            <Info className="h-4 w-4" />
                                                            <div className="alert-content">
                                                                <strong>DNS Configuration Required</strong>
                                                                <p className="mt-2 text-sm">Configure these DNS records at your domain provider:</p>

                                                                {/* Main CNAME record */}
                                                                {cnameUrl && (
                                                                    <div className="dns-record-item mt-3">
                                                                        <div className="dns-label">Point your domain to:</div>
                                                                        <div className="dns-value-row">
                                                                            <code className="dns-code">{cnameUrl}</code>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="copy-btn"
                                                                                onClick={() => handleCopy(cnameUrl, "CNAME URL")}
                                                                            >
                                                                                <Copy className="w-3 h-3" />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ACME Challenge CNAME */}
                                                                <div className="dns-record-item mt-3">
                                                                    <div className="dns-label">ACME Challenge Record:</div>
                                                                    <div className="dns-value-row">
                                                                        <code className="dns-code">{cert.CNAME?.instructions}</code>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="copy-btn"
                                                                            onClick={() => handleCopy(cert.CNAME?.instructions || "", "CNAME record")}
                                                                        >
                                                                            <Copy className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                <p className="mt-3 text-xs text-muted">
                                                                    After configuring these DNS records, the certificate will be automatically issued.
                                                                    Verification typically takes 5-10 minutes, but DNS propagation can take up to 24 hours in some cases.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {cert.error_message && (
                                                        <div className="alert-box error-alert mt-3">
                                                            <div className="alert-content text-sm">
                                                                {cert.error_message}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {cert.createdAt && (
                                                        <p className="certificate-date">
                                                            Added on {new Date(cert.createdAt).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="guide">
                            {/* DNS Provider Guide */}
                            <Card className="provider-guide-card">
                                <div className="card-content">
                                    <h3 className="text-lg font-semibold mb-2">DNS Configuration Guide</h3>
                                    <p className="text-sm text-muted mb-4">Select your DNS provider for step-by-step instructions</p>

                                    <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                                        <SelectTrigger className="w-full mb-4">
                                            <SelectValue placeholder="Choose your DNS provider..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DNS_PROVIDERS.map((provider) => (
                                                <SelectItem key={provider.id} value={provider.id}>
                                                    {provider.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    {selectedProvider && (() => {
                                        const provider = DNS_PROVIDERS.find(p => p.id === selectedProvider);
                                        if (!provider) return null;

                                        return (
                                            <div className="provider-instructions">
                                                <div className="steps-section">
                                                    <h4 className="section-title">Steps for {provider.name}</h4>
                                                    <ol className="steps-list">
                                                        {provider.steps.map((step, index) => (
                                                            <li key={index}>{step}</li>
                                                        ))}
                                                    </ol>
                                                </div>

                                                <div className="fields-section">
                                                    <h4 className="section-title">Fields to Fill</h4>
                                                    <div className="fields-list">
                                                        {provider.fields.map((field, index) => (
                                                            <div key={index} className="field-item">
                                                                <span className="field-label">{field.label}:</span>
                                                                <span className="field-value">{field.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {provider.notes && provider.notes.length > 0 && (
                                                    <div className="notes-section">
                                                        {provider.notes.map((note, index) => (
                                                            <div key={index} className="note-item">
                                                                {note}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
};

export default DomainsSettings;
